import type { QuestionFillGapsSchema } from '@repo/schemas/quiz/questionFillGaps';

import { cn } from '@repo/lib/cn';
import { getPointsLabel } from '@repo/lib/quiz/getPointsLabel';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { AnswerCorrectToggle, AnswerExplanationDialog, AnswerExplanationSection } from '@/components/features/quiz/question/question-builder';
import { Button } from '@/components/ui/button';
import { ErrorMessage, FormControl, FormField, FormItem } from '@/components/ui/form';
import { NumberSelect } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuestionFillGapForm } from '@/hooks/questions';

import type { QuestionFillFormGapProps } from '.';

interface DebouncedTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  debounceMs?: number;
}

function DebouncedTextarea(props: DebouncedTextareaProps) {
  const { value: externalValue, onChange, placeholder, className, debounceMs = 150 } = props;

  // Local state for immediate UI updates
  const [localValue, setLocalValue] = useState(externalValue);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInternalUpdate = useRef(false);

  // Sync external value to local state (only when external value changes)
  useEffect(() => {
    if (!isInternalUpdate.current) {
      setLocalValue(externalValue);
    }
    isInternalUpdate.current = false;
  }, [externalValue]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      // Update local state immediately for responsive UI
      setLocalValue(newValue);
      isInternalUpdate.current = true;

      // Debounce the form sync
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        onChange(newValue);
        timeoutRef.current = null;
      }, debounceMs);
    },
    [onChange, debounceMs]
  );

  return <Textarea value={localValue} onChange={handleChange} className={className} placeholder={placeholder} />;
}

interface GapOptionFieldProps {
  gapIndex: number;
  optionIndex: number;
  onToggleCorrect: (index: number) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

function GapOptionField(props: GapOptionFieldProps) {
  const { gapIndex, optionIndex, onToggleCorrect, onRemove, canRemove } = props;

  const [openExplanation, setOpenExplanation] = useState<boolean>(false);
  const form = useFormContext<QuestionFillGapsSchema>();
  const isCorrect = useWatch({
    control: form.control,
    name: `gaps.${gapIndex}.options.${optionIndex}.correct`,
  });

  return (
    <div className="bg-card flex w-full flex-col rounded-lg border shadow-sm">
      <div className="flex justify-between gap-3 border-b p-2">
        <FormItem className="w-fit">
          <FormControl>
            <AnswerCorrectToggle correct={Boolean(isCorrect)} onCorrectChange={() => onToggleCorrect(optionIndex)} />
          </FormControl>
        </FormItem>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" type="button" variant="outline" disabled={!canRemove} onClick={() => onRemove(optionIndex)}>
              <TrashIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Usuń odpowiedź</TooltipContent>
        </Tooltip>
      </div>

      <div className="flex flex-col gap-2 p-2">
        <FormField
          name={`gaps.${gapIndex}.options.${optionIndex}.content`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <DebouncedTextarea value={field.value || ''} onChange={field.onChange} className="border-0 shadow-none" placeholder="Wprowadź odpowiedź" />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          name={`gaps.${gapIndex}.options.${optionIndex}.explanation`}
          render={({ field }) => (
            <>
              <div className="mt-auto">
                <AnswerExplanationSection explanation={field.value} onOpenExplanation={() => setOpenExplanation(true)} />
              </div>
              <AnswerExplanationDialog
                open={openExplanation}
                onOpenChange={() => setOpenExplanation(false)}
                onRemoveExplanation={() => field.onChange(undefined)}
                onValueChange={(value) => field.onChange(value)}
                value={field.value}
              />
            </>
          )}
        />

        <ErrorMessage name={`gaps.${gapIndex}.options.${optionIndex}.content`} className="min-h-0 empty:hidden" />
      </div>
    </div>
  );
}

interface GapHeaderProps {
  gapIndex: number;
}

function GapHeader(props: GapHeaderProps) {
  const { gapIndex } = props;

  return (
    <div className="flex items-center justify-between gap-3 border-b p-2">
      <div className="flex gap-2">
        <span className="text-muted-foreground text-sm">Numer luki: </span>
        <span className="bg-gray text-gray-foreground flex size-4.5 items-center justify-center rounded-full text-xs">{gapIndex + 1}</span>
      </div>
      <FormField
        name={`gaps.${gapIndex}.points`}
        render={({ field }) => {
          return (
            <FormItem className="w-fit">
              <FormControl>
                <NumberSelect
                  value={field.value == null ? undefined : String(field.value)}
                  onValueChange={(value) => field.onChange(value === '' ? null : Number(value))}
                  start={1}
                  end={20}
                  getLabel={getPointsLabel}
                  placeholder="---"
                  className="w-38"
                  size="sm"
                />
              </FormControl>
            </FormItem>
          );
        }}
      />
    </div>
  );
}

export function QuestionFillFormGap(questionFillFormGapProps: QuestionFillFormGapProps) {
  const { gapIndex } = questionFillFormGapProps;
  const form = useFormContext<QuestionFillGapsSchema>();
  const { gapId, translateQuestionGapOptions, handleAppendAnswer, toggleCorrect, handleRemoveAnswer } = useQuestionFillGapForm(gapIndex);

  // Watch options to get indices
  const options = useWatch({
    control: form.control,
    name: `gaps.${gapIndex}.options`,
  });

  return (
    <div data-gap-form-id={gapId} className="bg-card divide-y rounded-lg border">
      <GapHeader gapIndex={gapIndex} />

      <div className={'space-y-3 p-2'}>
        <div className="flex items-center justify-between">
          <p className={'text-text-tertiary text-xs'}>Odpowiedzi do luki:</p>
        </div>

        <div className="flex flex-col items-stretch gap-3 xl:flex-row">
          {options?.map((_opt, index) => (
            <GapOptionField
              key={`option-${gapIndex}-${index}`}
              gapIndex={gapIndex}
              optionIndex={index}
              onToggleCorrect={toggleCorrect}
              onRemove={handleRemoveAnswer}
              canRemove={translateQuestionGapOptions.fields.length > 2}
            />
          ))}
          <Button
            type="button"
            variant={'outline'}
            size={'sm'}
            className={cn('border-dashed w-full xl:w-auto xl:my-auto', translateQuestionGapOptions.fields.length === 5 && 'hidden')}
            onClick={handleAppendAnswer}
          >
            <PlusIcon />
            <span className="block xl:hidden">Dodaj odpowiedź</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
